const RUSSIAN_TO_LATIN = {
  А: "A",
  В: "B",
  С: "C",
  Е: "E",
  Н: "H",
  К: "K",
  М: "M",
  О: "O",
  Р: "P",
  Т: "T",
  Х: "X",
  У: "Y",
};

const LATIN_TO_RUSSIAN = Object.fromEntries(
  Object.entries(RUSSIAN_TO_LATIN).map(([russianCharacter, latinCharacter]) => [latinCharacter, russianCharacter]),
);

const ALLOWED_PLATE_LETTERS = "ABCEHKMOPTXY";

const CAR_PLATE_TYPES = {
  civil: {
    value: "civil",
    example: "A000AA 777",
    mask: "A###AA ###",
    tokens: ["letter", "digit", "digit", "digit", "letter", "letter", " ", "digit", "digit", "digit"],
    validationPattern: /^[ABCEHKMOPTXY]\d{3}[ABCEHKMOPTXY]{2} \d{2,3}$/,
    background: "#ffffff",
    color: "#1f2430",
  },
  police: {
    value: "police",
    example: "A0000 777",
    mask: "A#### ###",
    tokens: ["letter", "digit", "digit", "digit", "digit", " ", "digit", "digit", "digit"],
    validationPattern: /^[ABCEHKMOPTXY]\d{4} \d{2,3}$/,
    background: "#2864a5",
    color: "#ffffff",
  },
  military: {
    value: "military",
    example: "0000AA 777",
    mask: "####AA ###",
    tokens: ["digit", "digit", "digit", "digit", "letter", "letter", " ", "digit", "digit", "digit"],
    validationPattern: /^\d{4}[ABCEHKMOPTXY]{2} \d{2,3}$/,
    background: "#242424",
    color: "#ffffff",
  },
  motorcycle: {
    value: "motorcycle",
    example: "0000AA 777",
    mask: "####AA ###",
    tokens: ["digit", "digit", "digit", "digit", "letter", "letter", " ", "digit", "digit", "digit"],
    validationPattern: /^\d{4}[ABCEHKMOPTXY]{2} \d{2,3}$/,
    background: "#ffffff",
    color: "#1f2430",
  },
  publicTransport: {
    value: "publicTransport",
    example: "AA000 777",
    mask: "AA### ###",
    tokens: ["letter", "letter", "digit", "digit", "digit", " ", "digit", "digit", "digit"],
    validationPattern: /^[ABCEHKMOPTXY]{2}\d{3} \d{2,3}$/,
    background: "#f2c94c",
    color: "#1f2430",
  },
  foreign: {
    value: "foreign",
    example: "XXXXXX",
    mask: null,
    tokens: null,
    background: "#ffffff",
    color: "#1f2430",
  },
};

const transliterateCarPlate = (value = "") =>
  String(value || "")
    .toUpperCase()
    .split("")
    .map(character => RUSSIAN_TO_LATIN[character] || character)
    .join("");

const displayCarPlateInCyrillic = (value = "", plateType) => {
  if (plateType === "foreign") return String(value || "");

  return String(value || "")
    .split("")
    .map(character => LATIN_TO_RUSSIAN[character] || character)
    .join("");
};

const isAllowedLetter = character => ALLOWED_PLATE_LETTERS.includes(character);
const tokenAcceptsCharacter = (token, character) =>
  (token === "letter" && isAllowedLetter(character)) || (token === "digit" && /\d/.test(character));

const formatRussianCarPlate = (value, plateType) => {
  const config = CAR_PLATE_TYPES[plateType];

  if (!config || !config.tokens) return "";

  const characters = transliterateCarPlate(value)
    .split("")
    .filter(character => /\d/.test(character) || isAllowedLetter(character));
  let characterIndex = 0;
  let result = "";

  config.tokens.forEach(token => {
    if (token === " ") {
      if (result && characterIndex < characters.length) result += " ";
      return;
    }

    while (characterIndex < characters.length && !tokenAcceptsCharacter(token, characters[characterIndex])) {
      characterIndex += 1;
    }

    if (characterIndex < characters.length) {
      result += characters[characterIndex];
      characterIndex += 1;
    }
  });

  return result;
};

const normalizeCarPlateValue = (value, plateType) => {
  if (plateType === "foreign") return String(value || "");
  return formatRussianCarPlate(value, plateType);
};

const isCompleteCarPlate = (value, plateType) => {
  if (!value) return false;
  if (plateType === "foreign") return true;

  const config = CAR_PLATE_TYPES[plateType];
  return Boolean(config?.validationPattern?.test(normalizeCarPlateValue(value, plateType)));
};

const normalizeCarPlatePayload = value => {
  const plateType = value && value.plate_type;

  return {
    value: normalizeCarPlateValue(value && value.value, plateType),
    plate_type: plateType,
  };
};

module.exports = {
  ALLOWED_PLATE_LETTERS,
  CAR_PLATE_TYPES,
  LATIN_TO_RUSSIAN,
  RUSSIAN_TO_LATIN,
  displayCarPlateInCyrillic,
  isCompleteCarPlate,
  normalizeCarPlatePayload,
  normalizeCarPlateValue,
  transliterateCarPlate,
};
